import React from 'react';
import { Link } from 'react-router-dom';
import { PageTitle } from '../components/PageTitle';
import { Helmet } from 'react-helmet-async';
import { ProfessionalJourney } from '../components/ProfessionalJourney';
import { BriefcaseIcon, BarChart2Icon, PaletteIcon, UsersIcon, TrendingUpIcon, CompassIcon, BrainIcon, GlobeIcon, HeartIcon, LightbulbIcon } from 'lucide-react';
export function About() {
  return <div className="bg-white" data-id="element-200">
      <PageTitle title="About Me" canonical="/about" description="Learn about Katalina Bock's background as a product and marketing leader with experience spanning Fortune 50, unicorn, and startup companies." keywords="about Katalina Bock, product leader, marketing leader, professional background, Harvard Business School, Duke University, Verkada, Productboard, Amazon, Johnson & Johnson" data-id="element-201" />
      <Helmet data-id="element-202">
        <script type="application/ld+json" data-id="element-203">
          {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Person',
          name: 'Katalina Bock',
          url: 'https://katalinabock.com',
          image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/93EkAeB1832rjEHAhKY7UP/Untitled_design_%281%29.png',
          jobTitle: 'Product Leader',
          worksFor: {
            '@type': 'Organization',
            name: 'Verkada'
          },
          alumniOf: [{
            '@type': 'CollegeOrUniversity',
            name: 'Harvard Business School'
          }, {
            '@type': 'CollegeOrUniversity',
            name: 'Duke University'
          }],
          knowsAbout: ['Product Management', 'Product Strategy', 'Growth', 'Innovation', 'Cross-Functional Leadership'],
          description: 'Product leader specializing in product strategy, growth, and innovation with impact across Fortune 50, unicorn, and startup companies.'
        })}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#E0F5F8] to-[#F0FAFC] text-gray-900 py-20" data-id="element-204">
        <div className="container mx-auto px-4" data-id="element-205">
          <div className="flex flex-col md:flex-row items-center" data-id="element-206">
            <div className="md:w-1/3 mb-8 md:mb-0 flex justify-center" data-id="element-207">
              <div className="w-64 h-64 rounded-full overflow-hidden border-4 border-[#7DCCD8]" data-id="element-208">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/93EkAeB1832rjEHAhKY7UP/Untitled_design_%281%29.png" alt="Katalina Bock headshot" className="w-full h-full object-cover grayscale" data-id="element-209" />
              </div>
            </div>
            <div className="md:w-2/3 md:pl-12" data-id="element-210">
              <h1 className="text-4xl font-bold mb-4" data-id="element-211">Katalina Bock</h1>
              <p className="text-xl mb-6 text-gray-700" data-id="element-212">
                Delivering growth, innovation, and market expansion across
                consumer-centric tech companies.
              </p>
              <p className="mb-6 text-gray-600" data-id="element-213">
                With experience spanning product management, product growth, and
                product marketing, I thrive at the intersection of product
                strategy, customer psychology, and market insights. I'm known
                for blending analytical rigor with creative storytelling and
                influencing executives to align on bold growth bets.
              </p>
              <div className="flex gap-4" data-id="element-214">
                <a href="https://www.linkedin.com/in/katalinabock" target="_blank" rel="noopener noreferrer" className="bg-[#0096B4] hover:bg-[#00778F] text-white px-6 py-3 rounded-md font-medium transition-colors duration-200 inline-flex items-center" data-id="element-215">
                  LinkedIn Profile
                </a>
                <Link to="/contact" className="bg-transparent border border-gray-700 hover:bg-gray-700 hover:text-white text-gray-700 px-6 py-3 rounded-md font-medium transition-colors duration-200" data-id="element-216">
                  Contact Me
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* My Journey Section - Replaced with new component */}
      <ProfessionalJourney data-id="element-217" />

      {/* Education Section */}
      <section className="py-10 bg-[#E0F5F8]" data-id="element-218">
        <div className="container mx-auto px-4" data-id="element-219">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-220">
            Education
          </h2>
          <div className="max-w-3xl mx-auto" data-id="element-221">
            {/* Harvard */}
            <div className="mb-6 bg-white p-4 rounded-lg shadow-md" data-id="element-222">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-2" data-id="element-223">
                <div className="flex items-center" data-id="element-224">
                  <div className="bg-[#E0F5F8] p-2 rounded-full mr-3 w-12 h-12 flex items-center justify-center" data-id="element-225">
                    <span className="text-[#0096B4] font-bold" data-id="element-226">MBA</span>
                  </div>
                  <h3 className="text-xl font-bold" data-id="element-227">Harvard Business School</h3>
                </div>
                <div className="mt-1 md:mt-0" data-id="element-228">
                  <span className="text-[#0096B4] font-medium" data-id="element-229">Boston, MA</span>
                </div>
              </div>
              <div className="ml-12" data-id="element-230">
                <p className="text-gray-800" data-id="element-231">
                  <span className="font-medium" data-id="element-232">
                    Master of Business Administration
                  </span>{' '}
                  (Management Science Focus)
                </p>
              </div>
            </div>
            {/* Duke */}
            <div className="bg-white p-4 rounded-lg shadow-md" data-id="element-233">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-2" data-id="element-234">
                <div className="flex items-center" data-id="element-235">
                  <div className="bg-[#E0F5F8] p-2 rounded-full mr-3 w-12 h-12 flex items-center justify-center" data-id="element-236">
                    <span className="text-[#0096B4] font-bold" data-id="element-237">BA</span>
                  </div>
                  <h3 className="text-xl font-bold" data-id="element-238">Duke University</h3>
                </div>
                <div className="mt-1 md:mt-0" data-id="element-239">
                  <span className="text-[#0096B4] font-medium" data-id="element-240">Durham, NC</span>
                </div>
              </div>
              <div className="ml-12" data-id="element-241">
                <p className="text-gray-800 font-medium" data-id="element-242">
                  B.A. in Psychology, Markets and Management, Dance Minor
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* What I Bring Section */}
      <section className="py-10 bg-white" data-id="element-243">
        <div className="container mx-auto px-4" data-id="element-244">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center" data-id="element-245">
            What I Bring to the Table
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto" data-id="element-246">
            <div className="bg-[#E0F5F8] p-4 rounded-lg shadow-md text-center" data-id="element-247">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-[#7DCCD8] text-[#0096B4] rounded-full mb-3" data-id="element-248">
                <CompassIcon size={28} data-id="element-249" />
              </div>
              <h3 className="text-xl font-semibold mb-2" data-id="element-250">Strategic Vision</h3>
              <p className="text-gray-600" data-id="element-251">
                I identify market opportunities and translate customer insights
                into compelling product strategies by fusing psychology, data,
                and market analysis to uncover the "why" behind behavior.
              </p>
            </div>
            <div className="bg-[#E0F5F8] p-4 rounded-lg shadow-md text-center" data-id="element-252">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-[#7DCCD8] text-[#0096B4] rounded-full mb-3" data-id="element-253">
                <UsersIcon size={28} data-id="element-254" />
              </div>
              <h3 className="text-xl font-semibold mb-2" data-id="element-255">
                Cross-Functional Leadership
              </h3>
              <p className="text-gray-600" data-id="element-256">
                I unite engineers, designers, marketers, and executives around a
                shared vision, aligning diverse perspectives to move complex
                products from idea to impact.
              </p>
            </div>
            <div className="bg-[#E0F5F8] p-4 rounded-lg shadow-md text-center" data-id="element-257">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-[#7DCCD8] text-[#0096B4] rounded-full mb-3" data-id="element-258">
                <LightbulbIcon size={28} data-id="element-259" />
              </div>
              <h3 className="text-xl font-semibold mb-2" data-id="element-260">
                Innovative Problem-Solving
              </h3>
              <p className="text-gray-600" data-id="element-261">
                I pair analytical rigor with creativity to generate
                out-of-the-box solutions that are both bold in vision and
                practical in delivery.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Technical Tools Section */}
      <section className="py-10 bg-[#E0F5F8]" data-id="element-262">
        <div className="container mx-auto px-4" data-id="element-263">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center" data-id="element-264">
            Technical Tools
          </h2>
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md p-5" data-id="element-265">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-266">
              <div data-id="element-267">
                <div className="flex items-center mb-2" data-id="element-268">
                  <div className="bg-[#0096B4] p-2 rounded-full mr-2" data-id="element-269">
                    <BriefcaseIcon size={16} className="text-white" data-id="element-270" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-271">
                    Product & Project
                  </h3>
                </div>
                <div className="flex flex-wrap gap-1 ml-8" data-id="element-272">
                  {['JIRA', 'Productboard', 'Monday', 'Notion', 'Asana', 'Basecamp', 'Miro'].map((tool, index) => <span key={index} className="bg-[#E0F5F8] text-[#0096B4] px-2 py-1 rounded-full text-xs font-medium mb-1" data-id="element-273">
                      {tool}
                    </span>)}
                </div>
              </div>
              <div data-id="element-274">
                <div className="flex items-center mb-2" data-id="element-275">
                  <div className="bg-[#0096B4] p-2 rounded-full mr-2" data-id="element-276">
                    <BarChart2Icon size={16} className="text-white" data-id="element-277" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-278">
                    Data & Analytics
                  </h3>
                </div>
                <div className="flex flex-wrap gap-1 ml-8" data-id="element-279">
                  {['SQL', 'Tableau', 'Power BI', 'Google Analytics', 'Excel', 'Looker'].map((tool, index) => <span key={index} className="bg-[#E0F5F8] text-[#0096B4] px-2 py-1 rounded-full text-xs font-medium mb-1" data-id="element-280">
                      {tool}
                    </span>)}
                </div>
              </div>
              <div data-id="element-281">
                <div className="flex items-center mb-2" data-id="element-282">
                  <div className="bg-[#0096B4] p-2 rounded-full mr-2" data-id="element-283">
                    <PaletteIcon size={16} className="text-white" data-id="element-284" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-285">
                    Design & Creative
                  </h3>
                </div>
                <div className="flex flex-wrap gap-1 ml-8" data-id="element-286">
                  {['Adobe Creative Cloud', 'Adobe Express', 'Lightroom', 'Canva', 'Magic Patterns', 'Framer', 'WordPress', 'Pinterest'].map((tool, index) => <span key={index} className="bg-[#E0F5F8] text-[#0096B4] px-2 py-1 rounded-full text-xs font-medium mb-1" data-id="element-287">
                      {tool}
                    </span>)}
                </div>
              </div>
              <div data-id="element-288">
                <div className="flex items-center mb-2" data-id="element-289">
                  <div className="bg-[#0096B4] p-2 rounded-full mr-2" data-id="element-290">
                    <TrendingUpIcon size={16} className="text-white" data-id="element-291" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-292">
                    CRM & Marketing
                  </h3>
                </div>
                <div className="flex flex-wrap gap-1 ml-8" data-id="element-293">
                  {['Salesforce', 'Marketo', 'Mailchimp', 'ActiveCampaign', 'SEO'].map((tool, index) => <span key={index} className="bg-[#E0F5F8] text-[#0096B4] px-2 py-1 rounded-full text-xs font-medium mb-1" data-id="element-294">
                      {tool}
                    </span>)}
                </div>
              </div>
              <div data-id="element-295">
                <div className="flex items-center mb-2" data-id="element-296">
                  <div className="bg-[#0096B4] p-2 rounded-full mr-2" data-id="element-297">
                    <BrainIcon size={16} className="text-white" data-id="element-298" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-299">
                    AI & Automation
                  </h3>
                </div>
                <div className="flex flex-wrap gap-1 ml-8" data-id="element-300">
                  {['Magic Patterns', 'Claude', 'ChatGPT', 'Gemini', 'Perplexity', 'Cursor', 'Xcode'].map((tool, index) => <span key={index} className="bg-[#E0F5F8] text-[#0096B4] px-2 py-1 rounded-full text-xs font-medium mb-1" data-id="element-301">
                      {tool}
                    </span>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Personal Section */}
      <section className="py-10 bg-white" data-id="element-302">
        <div className="container mx-auto px-4" data-id="element-303">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center" data-id="element-304">
            Beyond the Boardroom
          </h2>
          <div className="max-w-4xl mx-auto" data-id="element-305">
            {/* Cultural Background */}
            <div className="mb-6 bg-white p-4 rounded-lg shadow-md" data-id="element-306">
              <div className="flex items-center mb-3" data-id="element-307">
                <div className="bg-[#E0F5F8] p-2 rounded-full mr-3" data-id="element-308">
                  <GlobeIcon size={24} className="text-[#0096B4]" data-id="element-309" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900" data-id="element-310">
                  Global Citizen
                </h3>
              </div>
              <div className="pl-12" data-id="element-311">
                <p className="text-gray-700 mb-2" data-id="element-312">
                  As a dual citizen who has lived in Germany, Switzerland, and
                  the United States, I bring a truly global perspective to my
                  work and life.
                </p>
                <div className="flex flex-wrap gap-2 mb-2" data-id="element-313">
                  <span className="bg-[#0096B4] text-white px-3 py-1 rounded-full text-sm" data-id="element-314">
                    Native German
                  </span>
                  <span className="bg-[#0096B4] text-white px-3 py-1 rounded-full text-sm" data-id="element-315">
                    Fluent English
                  </span>
                  <span className="bg-[#7DCCD8] text-white px-3 py-1 rounded-full text-sm" data-id="element-316">
                    Conversational French
                  </span>
                  <span className="bg-[#7DCCD8] text-white px-3 py-1 rounded-full text-sm" data-id="element-317">
                    Conversational Spanish
                  </span>
                </div>
              </div>
            </div>
            {/* Active Engagements */}
            <div className="mb-6 bg-white p-4 rounded-lg shadow-md" data-id="element-318">
              <div className="flex items-center mb-3" data-id="element-319">
                <div className="bg-[#E0F5F8] p-2 rounded-full mr-3" data-id="element-320">
                  <HeartIcon size={24} className="text-[#0096B4]" data-id="element-321" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900" data-id="element-322">
                  Active Engagements
                </h3>
              </div>
              <div className="pl-12" data-id="element-323">
                <p className="text-gray-700 mb-2" data-id="element-324">
                  I'm passionate about giving back through mentorship and
                  community involvement:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2" data-id="element-325">
                  <div className="bg-[#F0FAFC] p-3 rounded-lg" data-id="element-326">
                    <h4 className="font-medium text-[#0096B4] mb-1" data-id="element-327">
                      Industry Mentor at HBS
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-328">
                      Guiding students at Harvard Business School in their
                      product management career paths.
                    </p>
                  </div>
                  <div className="bg-[#F0FAFC] p-3 rounded-lg" data-id="element-329">
                    <h4 className="font-medium text-[#0096B4] mb-1" data-id="element-330">
                      Admissions Interviewer at Duke
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-331">
                      Helping select the next generation of Duke University
                      students.
                    </p>
                  </div>
                  <div className="bg-[#F0FAFC] p-3 rounded-lg" data-id="element-332">
                    <h4 className="font-medium text-[#0096B4] mb-1" data-id="element-333">
                      Mentor at Twenty
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-334">
                      Supporting emerging professionals through the Twenty
                      platform.
                    </p>
                  </div>
                  <div className="bg-[#F0FAFC] p-3 rounded-lg" data-id="element-335">
                    <h4 className="font-medium text-[#0096B4] mb-1" data-id="element-336">
                      Women's Association Mentor at HBS
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-337">
                      Empowering women in business through dedicated mentorship.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* Creative Pursuits */}
            <div className="bg-white p-4 rounded-lg shadow-md" data-id="element-338">
              <div className="flex items-center mb-3" data-id="element-339">
                <div className="bg-[#E0F5F8] p-2 rounded-full mr-3" data-id="element-340">
                  <PaletteIcon size={24} className="text-[#0096B4]" data-id="element-341" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900" data-id="element-342">
                  Creative Expression
                </h3>
              </div>
              <div className="pl-12" data-id="element-343">
                <p className="text-gray-700 mb-2" data-id="element-344">
                  My creative spirit finds expression through multiple artistic
                  outlets:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-2" data-id="element-345">
                  <div className="bg-[#F0FAFC] p-3 rounded-lg" data-id="element-346">
                    <h4 className="font-medium text-[#0096B4] mb-1" data-id="element-347">
                      Life-long Dancer
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-348">
                      Performed at the London Olympics and continue to dance as
                      a form of expression and joy.
                    </p>
                  </div>
                  <div className="bg-[#F0FAFC] p-3 rounded-lg" data-id="element-349">
                    <h4 className="font-medium text-[#0096B4] mb-1" data-id="element-350">
                      Visual Artist
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-351">
                      Graphic designer and acrylic/mixed media painter with a
                      passion for visual storytelling.
                    </p>
                  </div>
                  <div className="bg-[#F0FAFC] p-3 rounded-lg" data-id="element-352">
                    <h4 className="font-medium text-[#0096B4] mb-1" data-id="element-353">Writer</h4>
                    <p className="text-gray-600 text-sm" data-id="element-354">
                      Poetry writer who finds meaning and beauty in the power of
                      words and language.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Certifications & Programs Section */}
      <section className="py-10 bg-[#E0F5F8]" data-id="element-355">
        <div className="container mx-auto px-4" data-id="element-356">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center" data-id="element-357">
            Certifications & Programs
          </h2>
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md p-5" data-id="element-358">
            <div className="columns-1 md:columns-3 gap-4" data-id="element-359">
              {[{
              name: 'Generative AI',
              provider: 'Microsoft'
            }, {
              name: 'Machine Learning',
              provider: 'Amazon'
            }, {
              name: 'Agile',
              provider: 'LinkedIn'
            }, {
              name: 'Data Analytics',
              provider: 'DataCamp'
            }, {
              name: 'Vibe Coding',
              provider: 'LinkedIn'
            }, {
              name: 'Product Management',
              provider: 'Aha!'
            }, {
              name: 'Technical Product Management',
              provider: 'LinkedIn'
            }, {
              name: 'Advanced SQL',
              provider: 'Afterskills'
            }, {
              name: 'CM1 Certified Marketer',
              provider: 'General Assembly'
            }, {
              name: 'YouTube Content Strategy',
              provider: 'YouTube'
            }, {
              name: 'Facebook Blueprint',
              provider: 'Facebook'
            }].map((cert, index) => <div key={index} className="mb-3 break-inside-avoid" data-id="element-360">
                  <div className="flex items-center" data-id="element-361">
                    <div className="w-2 h-2 rounded-full bg-[#0096B4] mr-2" data-id="element-362"></div>
                    <div data-id="element-363">
                      <h3 className="font-medium text-gray-800 text-sm" data-id="element-364">
                        {cert.name}
                      </h3>
                      <p className="text-gray-500 text-xs" data-id="element-365">{cert.provider}</p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>
    </div>;
}